import { useState, useEffect } from 'react';

interface BrandingConfig {
  name: string;
  logo: string;
  showTitle?: boolean;
}

interface BrandingConfigFile {
  default: BrandingConfig;
  domains: Record<string, BrandingConfig>;
}

/**
 * Hook to fetch and apply domain-specific branding configuration
 * Reads from /config/branding.json and matches current domain
 */
export function useBranding(): BrandingConfig {
  const [branding, setBranding] = useState<BrandingConfig>({
    name: 'Open Health Imaging Foundation',
    logo: '/ohif-logo.svg',
  });

  useEffect(() => {
    const fetchBranding = async () => {
      try {
        const response = await fetch('/branding.json');
        if (!response.ok) {
          console.warn('Branding config not found, using defaults');
          return;
        }

        const config: BrandingConfigFile = await response.json();
        const currentDomain = window.location.host; // e.g., "localhost:3000" or "example.com"

        console.log('Current domain:', currentDomain);
        console.log('Available branding configs:', Object.keys(config.domains));

        // Try exact domain match first
        let brandingConfig = config.domains[currentDomain];

        // If no exact match, try partial match (e.g., "example.com" matches "www.example.com")
        if (!brandingConfig) {
          const domainKey = Object.keys(config.domains).find(domain => {
            return currentDomain.includes(domain) || domain.includes(currentDomain);
          });

          if (domainKey) {
            brandingConfig = config.domains[domainKey];
            console.log(`Matched domain "${currentDomain}" to config key "${domainKey}"`);
          }
        }

        // Fall back to default if no match
        const finalBranding = brandingConfig || config.default;

        console.log('Using branding:', finalBranding);
        setBranding(finalBranding);
      } catch (error) {
        console.error('Failed to load branding configuration:', error);
        // Keep using default branding
      }
    };

    fetchBranding();
  }, []);

  return branding;
}
